import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronUp, Pencil, Plus, RefreshCw, Shield, Trash2 } from 'lucide-react';
import { client } from '../../api/client';
import type { MemberDto, OrchestraGroupDto } from '../../types/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ConfirmDialog, Toast } from '../ui/Dialog';
import type { ConfirmOptions } from '../ui/Dialog';
import {
  AdminTable,
  AdminTableScroll,
  AdminTd,
  AdminTh,
  AdminToolbar,
  FieldLabel,
  FilterInput,
  FilterSelect,
  FormDialog,
} from './AdminPrimitives';

const INSTRUMENTS = [
  'piccolo / dwarsfluit',
  'hobo / fagot',
  'klarinet',
  'sax alto',
  'sax tenor / sax bariton',
  'trompet',
  'hoorn',
  'trombone',
  'euphonium',
  'bas',
  'percussie',
];

type SortKey = 'name' | 'group' | 'instrument' | 'status' | 'role';

interface EditState {
  memberId: string;
  firstName: string;
  lastName: string;
  instrument: string;
  selectedGroups: string[];
}

const SortTh = styled(AdminTh)`
  cursor: pointer;
  user-select: none;
  &:hover { background: #f0f4ff; }
`;

const SortIcon = styled.span`
  display: inline-flex;
  flex-direction: column;
  margin-left: 0.3rem;
  vertical-align: middle;
  line-height: 1;
  gap: 1px;
  svg { display: block; }
`;

const MemberNameCell = styled(AdminTd)`
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
`;

const GroupsCell = styled(AdminTd)`
  min-width: 0;
`;

const InstrumentCell = styled(AdminTd)`
  color: #555;
  white-space: nowrap;
`;

const ActionsCell = styled(AdminTd)`
  white-space: nowrap;
`;

const BadgeWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const Badge = styled.span<{ $type: 'success' | 'warning' | 'info' | 'default' | 'group' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: bold;
  background-color: ${({ $type }) =>
    $type === 'success'
      ? '#dcfce7'
      : $type === 'warning'
        ? '#fef9c3'
        : $type === 'info'
          ? '#dbeafe'
          : $type === 'group'
            ? '#ede9fe'
            : '#f3f4f6'};
  color: ${({ $type }) =>
    $type === 'success'
      ? '#166534'
      : $type === 'warning'
        ? '#854d0e'
        : $type === 'info'
          ? '#1e40af'
          : $type === 'group'
            ? '#5b21b6'
            : '#374151'};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 0.25rem;
  margin-right: 0.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.primary.royalBlue};
  }
`;

const TotalsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TotalChip = styled.span`
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.78rem;
  color: #374151;
  white-space: nowrap;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  cursor: pointer;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
`;

export const MembersTab = () => {
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [groups, setGroups] = useState<OrchestraGroupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importNames, setImportNames] = useState('');
  const [importGroups, setImportGroups] = useState<string[]>([]);
  const [importInstrument, setImportInstrument] = useState('');
  const [editState, setEditState] = useState<EditState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmOptions | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterInstrument, setFilterInstrument] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [memberSortKey, setMemberSortKey] = useState<SortKey>('name');
  const [memberSortDir, setMemberSortDir] = useState<'asc' | 'desc'>('asc');

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const showConfirm = useCallback((opts: Omit<ConfirmOptions, 'onCancel'> & { onCancel?: () => void }) => {
    setConfirm({ onCancel: () => setConfirm(null), ...opts });
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await client.get<MemberDto[]>('/members');
      setMembers(res.data);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Ophalen leden mislukt'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await client.get<OrchestraGroupDto[]>('/members/groups');
      setGroups(res.data);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Ophalen groepen mislukt'), 'error');
    }
  }, [showToast]);

  useEffect(() => {
    fetchMembers();
    fetchGroups();
  }, [fetchGroups, fetchMembers]);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return members.filter((m) => {
      if (q && !`${m.firstName} ${m.lastName}`.toLowerCase().includes(q)) return false;
      if (filterGroup && !m.groups.some((g) => g.id === filterGroup)) return false;
      if (filterInstrument && m.instrument !== filterInstrument) return false;
      if (filterStatus === 'activated' && !m.isActivated) return false;
      if (filterStatus === 'not_activated' && m.isActivated) return false;
      if (filterRole === 'admin' && !m.isAdmin) return false;
      if (filterRole === 'lid' && m.isAdmin) return false;
      return true;
    });
  }, [members, memberSearch, filterGroup, filterInstrument, filterStatus, filterRole]);

  const memberTotals = useMemo(() => {
    const groupCounts = new Map<string, number>();
    const instrumentCounts = new Map<string, number>();
    for (const member of members) {
      for (const group of member.groups) groupCounts.set(group.name, (groupCounts.get(group.name) ?? 0) + 1);
      if (member.instrument) instrumentCounts.set(member.instrument, (instrumentCounts.get(member.instrument) ?? 0) + 1);
    }
    return { groupCounts, instrumentCounts };
  }, [members]);

  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      const values: Record<SortKey, [string, string]> = {
        name: [`${a.lastName} ${a.firstName}`.toLowerCase(), `${b.lastName} ${b.firstName}`.toLowerCase()],
        group: [a.groups[0]?.name ?? '', b.groups[0]?.name ?? ''],
        instrument: [a.instrument ?? '', b.instrument ?? ''],
        status: [a.isActivated ? 'a' : 'b', b.isActivated ? 'a' : 'b'],
        role: [a.isAdmin ? 'a' : 'b', b.isAdmin ? 'a' : 'b'],
      };
      const [av, bv] = values[memberSortKey];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return memberSortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredMembers, memberSortKey, memberSortDir]);

  const handleMemberSort = (key: SortKey) => {
    if (memberSortKey === key) {
      setMemberSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setMemberSortKey(key);
    setMemberSortDir('asc');
  };

  const hasActiveFilters = memberSearch || filterGroup || filterInstrument || filterStatus || filterRole;

  const handleImport = async () => {
    const newMembers = importNames
      .split('\n')
      .filter((line) => line.trim())
      .map((line) => {
        const [first, ...rest] = line.trim().split(' ');
        return {
          firstName: first,
          lastName: rest.join(' '),
          groups: importGroups,
          instrument: importInstrument || undefined,
        };
      });

    try {
      await client.post('/members', { members: newMembers });
      setShowImportModal(false);
      setImportNames('');
      setImportGroups([]);
      setImportInstrument('');
      fetchMembers();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Importeren van leden mislukt'), 'error');
    }
  };

  const handleToggleAdmin = (id: string, currentStatus: boolean) => {
    showConfirm({
      title: currentStatus ? 'Bestuurslid verwijderen' : 'Bestuurslid maken',
      message: currentStatus
        ? 'Weet je zeker dat je dit lid als bestuurslid wilt verwijderen?'
        : 'Weet je zeker dat je dit lid bestuurslid wilt maken?',
      confirmLabel: 'Bevestigen',
      variant: currentStatus ? 'danger' : 'info',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await client.patch(`/members/${id}`, { isAdmin: !currentStatus });
          fetchMembers();
        } catch (error) {
          showToast(getApiErrorMessage(error, 'Status wijzigen mislukt'), 'error');
        }
      },
    });
  };

  const handleResetPasscode = (id: string) => {
    showConfirm({
      title: 'Code resetten',
      message: 'De gebruiker moet opnieuw activeren na het resetten van de code.',
      confirmLabel: 'Resetten',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await client.patch(`/members/${id}`, { resetPasscode: true });
          fetchMembers();
        } catch (error) {
          showToast(getApiErrorMessage(error, 'Code resetten mislukt'), 'error');
        }
      },
    });
  };

  const handleDeleteMember = (id: string, name: string) => {
    showConfirm({
      title: 'Lid verwijderen',
      message: `Weet je zeker dat je ${name} wilt verwijderen? Alle gegevens, inclusief aanwezigheidshistoriek, worden permanent gewist.`,
      confirmLabel: 'Verwijderen',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await client.delete(`/members/${id}`);
          setMembers((prev) => prev.filter((m) => m.id !== id));
          showToast(`${name} is verwijderd`, 'success');
        } catch (error) {
          showToast(getApiErrorMessage(error, 'Verwijderen mislukt'), 'error');
        }
      },
    });
  };

  const openEditModal = (member: MemberDto) => {
    setEditState({
      memberId: member.id,
      firstName: member.firstName,
      lastName: member.lastName,
      instrument: member.instrument ?? '',
      selectedGroups: member.groups.map((group) => group.id),
    });
  };

  const hasInstrumentGroup = (ids: string[]) =>
    ids.some((groupId) => {
      const groupName = groups.find((group) => group.id === groupId)?.name;
      return groupName === 'Harmonie' || groupName === 'Précilia';
    });

  const handleSaveEdit = async () => {
    if (!editState) return;
    try {
      await client.patch(`/members/${editState.memberId}`, {
        groups: editState.selectedGroups,
        instrument: hasInstrumentGroup(editState.selectedGroups) ? editState.instrument : '',
      });
      setEditState(null);
      fetchMembers();
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Opslaan mislukt'), 'error');
    }
  };

  const toggleImportGroup = (id: string) =>
    setImportGroups((prev) => (prev.includes(id) ? prev.filter((groupId) => groupId !== id) : [...prev, id]));

  const toggleEditGroup = (id: string) => {
    setEditState((prev) =>
      prev
        ? {
            ...prev,
            selectedGroups: prev.selectedGroups.includes(id)
              ? prev.selectedGroups.filter((groupId) => groupId !== id)
              : [...prev.selectedGroups, id],
          }
        : prev
    );
  };

  const importHasHarmonie = hasInstrumentGroup(importGroups);
  const editHasHarmonie = editState ? hasInstrumentGroup(editState.selectedGroups) : false;

  return (
    <>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Leden Overzicht</h2>
          <Button onClick={() => setShowImportModal(true)}>
            <Plus size={16} style={{ marginRight: '0.5rem' }} /> Importeer Leden
          </Button>
        </div>

        {!isLoading && (
          <>
            <TotalsBar>
              <TotalChip>Totaal: {members.length}</TotalChip>
              {[...memberTotals.groupCounts.entries()].map(([name, count]) => (
                <TotalChip key={name}>{name}: {count}</TotalChip>
              ))}
              {[...memberTotals.instrumentCounts.entries()].map(([name, count]) => (
                <TotalChip key={name}>{name}: {count}</TotalChip>
              ))}
            </TotalsBar>

            <AdminToolbar>
              <FilterInput
                type="search"
                placeholder="Zoek op naam..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
              <FilterSelect value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                <option value="">Alle groepen</option>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
              </FilterSelect>
              <FilterSelect value={filterInstrument} onChange={(e) => setFilterInstrument(e.target.value)}>
                <option value="">Alle instrumenten</option>
                {INSTRUMENTS.map((instrument) => <option key={instrument} value={instrument}>{instrument}</option>)}
              </FilterSelect>
              <FilterSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">Alle statussen</option>
                <option value="activated">Geactiveerd</option>
                <option value="not_activated">Niet geactiveerd</option>
              </FilterSelect>
              <FilterSelect value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <option value="">Alle rollen</option>
                <option value="admin">Bestuur</option>
                <option value="lid">Lid</option>
              </FilterSelect>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={() => { setMemberSearch(''); setFilterGroup(''); setFilterInstrument(''); setFilterStatus(''); setFilterRole(''); }}>
                  Wis filters
                </Button>
              )}
            </AdminToolbar>
          </>
        )}

        {isLoading ? (
          <p>Laden...</p>
        ) : (
          <AdminTableScroll>
            <AdminTable style={{ minWidth: 980, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '19%' }} />
                <col style={{ width: '28%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '13%' }} />
              </colgroup>
              <thead>
                <tr>
                  {(['name', 'group', 'instrument', 'status', 'role'] as const).map((col) => {
                    const labels = { name: 'Naam', group: 'Groepen', instrument: 'Instrument', status: 'Status', role: 'Rol' };
                    const active = memberSortKey === col;
                    return (
                      <SortTh key={col} onClick={() => handleMemberSort(col)}>
                        {labels[col]}
                        <SortIcon>
                          <ChevronUp size={11} color={active && memberSortDir === 'asc' ? '#1e40af' : '#cbd5e1'} />
                          <ChevronDown size={11} color={active && memberSortDir === 'desc' ? '#1e40af' : '#cbd5e1'} />
                        </SortIcon>
                      </SortTh>
                    );
                  })}
                  <AdminTh>Acties</AdminTh>
                </tr>
              </thead>
              <tbody>
                {sortedMembers.length === 0 && (
                  <tr><AdminTd colSpan={6} style={{ color: '#9ca3af', textAlign: 'center' }}>Geen leden gevonden.</AdminTd></tr>
                )}
                {sortedMembers.map((member) => (
                  <tr key={member.id} style={member.isAdmin ? { backgroundColor: '#f8fafc' } : {}}>
                    <MemberNameCell>{member.firstName} {member.lastName}</MemberNameCell>
                    <GroupsCell>
                      {member.groups.length > 0 ? (
                        <BadgeWrap>
                          {member.groups.map((group) => (
                            <Badge key={group.id} $type="group">{group.name}</Badge>
                          ))}
                        </BadgeWrap>
                      ) : <span style={{ color: '#aaa', fontSize: '0.85rem' }}>-</span>}
                    </GroupsCell>
                    <InstrumentCell style={{ fontSize: '0.9rem' }}>
                      {member.instrument || <span style={{ color: '#aaa' }}>-</span>}
                    </InstrumentCell>
                    <AdminTd>{member.isActivated ? <Badge $type="success">Geactiveerd</Badge> : <Badge $type="warning">Niet geactiveerd</Badge>}</AdminTd>
                    <AdminTd>{member.isAdmin ? <Badge $type="info">Bestuur</Badge> : <Badge $type="default">Lid</Badge>}</AdminTd>
                    <ActionsCell>
                      <ActionButton onClick={() => openEditModal(member)} title="Groepen / instrument">
                        <Pencil size={18} />
                      </ActionButton>
                      <ActionButton onClick={() => handleToggleAdmin(member.id, member.isAdmin)} title={member.isAdmin ? 'Verwijder Bestuur' : 'Maak Bestuur'}>
                        <Shield size={18} fill={member.isAdmin ? 'currentColor' : 'none'} />
                      </ActionButton>
                      <ActionButton onClick={() => handleResetPasscode(member.id)} title="Reset Code">
                        <RefreshCw size={18} />
                      </ActionButton>
                      <ActionButton onClick={() => handleDeleteMember(member.id, `${member.firstName} ${member.lastName}`)} title="Verwijder lid" style={{ color: '#dc2626' }}>
                        <Trash2 size={18} />
                      </ActionButton>
                    </ActionsCell>
                  </tr>
                ))}
              </tbody>
            </AdminTable>
          </AdminTableScroll>
        )}
      </Card>

      {showImportModal && (
        <FormDialog title="Importeer Leden" submitLabel="Importeer" onCancel={() => setShowImportModal(false)} onSubmit={handleImport}>
          <div style={{ marginBottom: '1rem' }}>
            <FieldLabel>Namen (één per regel, Voornaam Achternaam)</FieldLabel>
            <Textarea
              rows={6}
              value={importNames}
              onChange={(e) => setImportNames(e.target.value)}
              placeholder={'Jan Janssen\nPiet Peters'}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <FieldLabel>Groepen (voor alle geïmporteerde leden)</FieldLabel>
            <CheckboxGroup>
              {groups.map((group) => (
                <CheckboxRow key={group.id}>
                  <input type="checkbox" checked={importGroups.includes(group.id)} onChange={() => toggleImportGroup(group.id)} />
                  {group.name}
                </CheckboxRow>
              ))}
            </CheckboxGroup>
          </div>

          {importHasHarmonie && (
            <div style={{ marginBottom: '1rem' }}>
              <FieldLabel>Instrument (Harmonie / Precilia)</FieldLabel>
              <Select value={importInstrument} onChange={(e) => setImportInstrument(e.target.value)}>
                <option value="">- kies instrument -</option>
                {INSTRUMENTS.map((instrument) => <option key={instrument} value={instrument}>{instrument}</option>)}
              </Select>
            </div>
          )}
        </FormDialog>
      )}

      {editState && (
        <FormDialog title={`${editState.firstName} ${editState.lastName}`} onCancel={() => setEditState(null)} onSubmit={handleSaveEdit}>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>Groepen en instrument bewerken</p>

          <div style={{ marginBottom: '1rem' }}>
            <FieldLabel>Groepen</FieldLabel>
            <CheckboxGroup>
              {groups.map((group) => (
                <CheckboxRow key={group.id}>
                  <input type="checkbox" checked={editState.selectedGroups.includes(group.id)} onChange={() => toggleEditGroup(group.id)} />
                  {group.name}
                </CheckboxRow>
              ))}
            </CheckboxGroup>
          </div>

          {editHasHarmonie && (
            <div style={{ marginBottom: '1rem' }}>
              <FieldLabel>Instrument (Harmonie / Precilia)</FieldLabel>
              <Select value={editState.instrument} onChange={(e) => setEditState((prev) => prev ? { ...prev, instrument: e.target.value } : prev)}>
                <option value="">- kies instrument -</option>
                {INSTRUMENTS.map((instrument) => <option key={instrument} value={instrument}>{instrument}</option>)}
              </Select>
            </div>
          )}
        </FormDialog>
      )}

      {confirm && <ConfirmDialog {...confirm} />}
      {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
    </>
  );
};
